import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { 
  Plus, 
  Pencil, 
  Trash2, 
  UtensilsCrossed,
  Search,
  X,
  Eye,
  EyeOff,
  ChefHat,
  Store,
  Clock,
  Star,
  Leaf,
  Coffee,
  Wine
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Card, CardContent } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { Switch } from '../../components/ui/switch';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const menuCategories = [
  { value: 'breakfast', label: 'Breakfast', icon: Coffee },
  { value: 'lunch', label: 'Lunch', icon: UtensilsCrossed },
  { value: 'dinner', label: 'Dinner', icon: Wine },
  { value: 'local', label: 'Local Flavours', icon: Leaf },
  { value: 'drinks', label: 'Drinks', icon: Wine },
  { value: 'desserts', label: 'Desserts', icon: Coffee },
];

// ============== RESTAURANTS TAB ==============
function RestaurantsTab() {
  const { token } = useAuth();
  const [restaurants, setRestaurants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [featureInput, setFeatureInput] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    image: '',
    hours: '',
    cuisine: '',
    dress_code: 'Smart Casual',
    features: [],
    order: 0
  });

  const fetchRestaurants = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/dining/restaurants/all`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRestaurants(response.data);
    } catch (error) {
      toast.error('Failed to fetch restaurants');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRestaurants();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingItem) {
        await axios.put(`${API_URL}/api/dining/restaurants/${editingItem.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Restaurant updated successfully');
      } else {
        await axios.post(`${API_URL}/api/dining/restaurants`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Restaurant created successfully');
      }
      setIsDialogOpen(false);
      resetForm();
      fetchRestaurants();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save restaurant');
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      name: item.name,
      description: item.description,
      image: item.image,
      hours: item.hours,
      cuisine: item.cuisine,
      dress_code: item.dress_code || 'Smart Casual',
      features: item.features || [],
      order: item.order || 0
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this restaurant?')) return;
    try {
      await axios.delete(`${API_URL}/api/dining/restaurants/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Restaurant deleted successfully');
      fetchRestaurants();
    } catch (error) {
      toast.error('Failed to delete restaurant');
    }
  };

  const toggleActive = async (item) => {
    try {
      await axios.put(`${API_URL}/api/dining/restaurants/${item.id}`, 
        { is_active: !item.is_active },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchRestaurants();
    } catch (error) {
      toast.error('Failed to update restaurant');
    }
  };

  const addFeature = () => {
    if (featureInput.trim()) {
      setFormData({ ...formData, features: [...formData.features, featureInput.trim()] });
      setFeatureInput('');
    }
  };

  const removeFeature = (index) => {
    setFormData({ ...formData, features: formData.features.filter((_, i) => i !== index) });
  };

  const resetForm = () => {
    setEditingItem(null);
    setFormData({
      name: '',
      description: '',
      image: '',
      hours: '',
      cuisine: '',
      dress_code: 'Smart Casual',
      features: [],
      order: 0
    });
    setFeatureInput('');
  };

  if (loading) return <div className="flex items-center justify-center h-64">Loading...</div>;

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <p className="text-muted-foreground">{restaurants.length} restaurants</p>
        <Dialog open={isDialogOpen} onOpenChange={(open) => { setIsDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="btn-emerald" data-testid="add-restaurant-btn">
              <Plus className="w-4 h-4 mr-2" />
              Add Restaurant
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingItem ? 'Edit Restaurant' : 'Add New Restaurant'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label>Restaurant Name *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., Emerald Dining Room"
                  required
                  data-testid="restaurant-name-input"
                />
              </div>
              <div>
                <Label>Description *</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Brief description of the restaurant"
                  required
                />
              </div>
              <div>
                <Label>Image URL *</Label>
                <Input
                  value={formData.image}
                  onChange={(e) => setFormData({ ...formData, image: e.target.value })}
                  placeholder="https://..."
                  required
                />
                {formData.image && (
                  <img src={formData.image} alt="Preview" className="mt-2 h-32 w-full object-cover rounded-lg" />
                )}
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Operating Hours *</Label>
                  <Input
                    value={formData.hours}
                    onChange={(e) => setFormData({ ...formData, hours: e.target.value })}
                    placeholder="e.g., 7:00 AM - 10:30 PM"
                    required
                  />
                </div>
                <div>
                  <Label>Cuisine Type *</Label>
                  <Input
                    value={formData.cuisine}
                    onChange={(e) => setFormData({ ...formData, cuisine: e.target.value })}
                    placeholder="e.g., Multi-Cuisine"
                    required
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Dress Code</Label>
                  <Select value={formData.dress_code} onValueChange={(v) => setFormData({ ...formData, dress_code: v })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Casual">Casual</SelectItem>
                      <SelectItem value="Smart Casual">Smart Casual</SelectItem>
                      <SelectItem value="Formal">Formal</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Display Order</Label>
                  <Input
                    type="number"
                    value={formData.order}
                    onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) || 0 })}
                  />
                </div>
              </div>
              <div>
                <Label>Features</Label>
                <div className="flex gap-2 mb-2">
                  <Input
                    value={featureInput}
                    onChange={(e) => setFeatureInput(e.target.value)}
                    placeholder="Add feature (e.g., Live Kitchen)"
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addFeature())}
                  />
                  <Button type="button" variant="outline" onClick={addFeature}>Add</Button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {formData.features.map((feature, idx) => (
                    <Badge key={idx} variant="secondary" className="flex items-center gap-1">
                      {feature}
                      <X className="w-3 h-3 cursor-pointer" onClick={() => removeFeature(idx)} />
                    </Badge>
                  ))}
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>Cancel</Button>
                <Button type="submit" className="btn-emerald" data-testid="save-restaurant-btn">
                  {editingItem ? 'Update' : 'Create'} Restaurant
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid gap-4">
        {restaurants.map((item, index) => (
          <motion.div
            key={item.id}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.05 }}
          >
            <Card className={!item.is_active ? 'opacity-60' : ''} data-testid={`restaurant-card-${item.id}`}>
              <CardContent className="p-4">
                <div className="flex gap-4">
                  <div className="w-32 h-24 rounded-lg overflow-hidden flex-shrink-0">
                    <img src={item.image} alt={item.name} className="w-full h-full object-cover" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="font-serif text-lg">{item.name}</h3>
                        <div className="flex items-center gap-2 text-sm text-muted-foreground mt-1">
                          <Clock className="w-4 h-4" />
                          {item.hours}
                          <span>•</span>
                          {item.cuisine}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={item.is_active}
                          onCheckedChange={() => toggleActive(item)}
                        />
                        <Button variant="outline" size="sm" onClick={() => handleEdit(item)}>
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button variant="outline" size="sm" className="text-red-600" onClick={() => handleDelete(item.id)}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                    <p className="text-sm text-muted-foreground mt-2 line-clamp-1">{item.description}</p>
                    {item.features?.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-2">
                        {item.features.map((f, i) => (
                          <Badge key={i} variant="outline" className="text-xs">{f}</Badge>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        ))}
      </div>
    </div>
  );
}

// ============== MENU TAB ==============
function MenuTab() {
  const { token } = useAuth();
  const [menuItems, setMenuItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [filterCategory, setFilterCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: 0,
    category: 'lunch',
    is_signature: false,
    is_vegetarian: false,
    order: 0
  });

  const fetchMenuItems = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/dining/menu/all`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMenuItems(response.data);
    } catch (error) {
      toast.error('Failed to fetch menu items');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMenuItems();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingItem) {
        await axios.put(`${API_URL}/api/dining/menu/${editingItem.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Menu item updated successfully');
      } else {
        await axios.post(`${API_URL}/api/dining/menu`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Menu item created successfully');
      }
      setIsDialogOpen(false);
      resetForm();
      fetchMenuItems();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save menu item');
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      name: item.name,
      description: item.description,
      price: item.price,
      category: item.category,
      is_signature: item.is_signature || false,
      is_vegetarian: item.is_vegetarian || false,
      order: item.order || 0
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this menu item?')) return;
    try {
      await axios.delete(`${API_URL}/api/dining/menu/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Menu item deleted successfully');
      fetchMenuItems();
    } catch (error) {
      toast.error('Failed to delete menu item');
    }
  };

  const toggleAvailable = async (item) => {
    try {
      await axios.put(`${API_URL}/api/dining/menu/${item.id}`, 
        { is_available: !item.is_available },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchMenuItems();
    } catch (error) {
      toast.error('Failed to update menu item');
    }
  };

  const resetForm = () => {
    setEditingItem(null);
    setFormData({
      name: '',
      description: '',
      price: 0,
      category: 'lunch',
      is_signature: false,
      is_vegetarian: false,
      order: 0
    });
  };

  const filteredItems = menuItems.filter(item => {
    const matchesCategory = filterCategory === 'all' || item.category === filterCategory;
    const matchesSearch = item.name.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  // Group items by category for display
  const groupedItems = filteredItems.reduce((acc, item) => {
    if (!acc[item.category]) acc[item.category] = [];
    acc[item.category].push(item);
    return acc;
  }, {});

  if (loading) return <div className="flex items-center justify-center h-64">Loading...</div>;

  return (
    <div>
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div className="flex items-center gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search menu..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 w-[200px]"
            />
          </div>
          <Select value={filterCategory} onValueChange={setFilterCategory}>
            <SelectTrigger className="w-[150px]">
              <SelectValue placeholder="Category" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {menuCategories.map(cat => (
                <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={(open) => { setIsDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="btn-emerald" data-testid="add-menu-item-btn">
              <Plus className="w-4 h-4 mr-2" />
              Add Menu Item
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>{editingItem ? 'Edit Menu Item' : 'Add New Menu Item'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label>Dish Name *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., Koraput Chicken Curry"
                  required
                  data-testid="menu-item-name-input"
                />
              </div>
              <div>
                <Label>Description *</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Brief description of the dish"
                  required
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Price (₹) *</Label>
                  <Input
                    type="number"
                    value={formData.price}
                    onChange={(e) => setFormData({ ...formData, price: parseInt(e.target.value) || 0 })}
                    required
                  />
                </div>
                <div>
                  <Label>Category *</Label>
                  <Select value={formData.category} onValueChange={(v) => setFormData({ ...formData, category: v })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {menuCategories.map(cat => (
                        <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="flex items-center gap-6">
                <div className="flex items-center gap-2">
                  <Switch
                    checked={formData.is_signature}
                    onCheckedChange={(v) => setFormData({ ...formData, is_signature: v })}
                  />
                  <Label className="cursor-pointer">Signature Dish</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Switch
                    checked={formData.is_vegetarian}
                    onCheckedChange={(v) => setFormData({ ...formData, is_vegetarian: v })}
                  />
                  <Label className="cursor-pointer">Vegetarian</Label>
                </div>
              </div>
              <div>
                <Label>Display Order</Label>
                <Input
                  type="number"
                  value={formData.order}
                  onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) || 0 })}
                />
              </div>
              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>Cancel</Button>
                <Button type="submit" className="btn-emerald" data-testid="save-menu-item-btn">
                  {editingItem ? 'Update' : 'Create'} Item
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <p className="text-muted-foreground mb-4">{filteredItems.length} menu items</p>

      {Object.entries(groupedItems).map(([category, items]) => (
        <div key={category} className="mb-8">
          <h3 className="font-serif text-lg capitalize mb-3 flex items-center gap-2">
            {menuCategories.find(c => c.value === category)?.label || category}
            <Badge variant="outline">{items.length}</Badge>
          </h3>
          <div className="grid gap-2">
            {items.map((item) => (
              <Card key={item.id} className={!item.is_available ? 'opacity-60' : ''} data-testid={`menu-item-${item.id}`}>
                <CardContent className="p-3">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{item.name}</span>
                        {item.is_signature && <Badge className="bg-gold text-emerald-950 text-xs"><Star className="w-3 h-3 mr-1" />Signature</Badge>}
                        {item.is_vegetarian && <Badge variant="outline" className="text-green-600 text-xs"><Leaf className="w-3 h-3 mr-1" />Veg</Badge>}
                      </div>
                      <p className="text-sm text-muted-foreground">{item.description}</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="font-serif text-lg">₹{item.price}</span>
                      <Switch
                        checked={item.is_available}
                        onCheckedChange={() => toggleAvailable(item)}
                      />
                      <Button variant="ghost" size="sm" onClick={() => handleEdit(item)}>
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="sm" className="text-red-600" onClick={() => handleDelete(item.id)}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      ))}

      {filteredItems.length === 0 && (
        <div className="text-center py-12 text-muted-foreground">
          <UtensilsCrossed className="w-12 h-12 mx-auto mb-4 opacity-50" />
          <p>No menu items found</p>
        </div>
      )}
    </div>
  );
}

// ============== CHEFS TAB ==============
function ChefsTab() {
  const { token } = useAuth();
  const [chefs, setChefs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    role: '',
    image: '',
    specialty: '',
    bio: '',
    order: 0
  });

  const fetchChefs = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/dining/chefs/all`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setChefs(response.data);
    } catch (error) {
      toast.error('Failed to fetch chefs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchChefs();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingItem) {
        await axios.put(`${API_URL}/api/dining/chefs/${editingItem.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Chef profile updated successfully');
      } else {
        await axios.post(`${API_URL}/api/dining/chefs`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Chef profile created successfully');
      }
      setIsDialogOpen(false);
      resetForm();
      fetchChefs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save chef profile');
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      name: item.name,
      role: item.role,
      image: item.image,
      specialty: item.specialty,
      bio: item.bio || '',
      order: item.order || 0
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this chef profile?')) return;
    try {
      await axios.delete(`${API_URL}/api/dining/chefs/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Chef profile deleted successfully');
      fetchChefs();
    } catch (error) {
      toast.error('Failed to delete chef profile');
    }
  };

  const toggleActive = async (item) => {
    try {
      await axios.put(`${API_URL}/api/dining/chefs/${item.id}`, 
        { is_active: !item.is_active },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchChefs();
    } catch (error) {
      toast.error('Failed to update chef profile');
    }
  };

  const resetForm = () => {
    setEditingItem(null);
    setFormData({
      name: '',
      role: '',
      image: '',
      specialty: '',
      bio: '',
      order: 0
    });
  };

  if (loading) return <div className="flex items-center justify-center h-64">Loading...</div>;

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <p className="text-muted-foreground">{chefs.length} chefs</p>
        <Dialog open={isDialogOpen} onOpenChange={(open) => { setIsDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="btn-emerald" data-testid="add-chef-btn">
              <Plus className="w-4 h-4 mr-2" />
              Add Chef
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>{editingItem ? 'Edit Chef Profile' : 'Add New Chef'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label>Chef Name *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., Chef Rajesh Mohanty"
                  required
                  data-testid="chef-name-input"
                />
              </div>
              <div>
                <Label>Role *</Label>
                <Input
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                  placeholder="e.g., Executive Chef"
                  required
                />
              </div>
              <div>
                <Label>Photo URL *</Label>
                <Input
                  value={formData.image}
                  onChange={(e) => setFormData({ ...formData, image: e.target.value })}
                  placeholder="https://..."
                  required
                />
                {formData.image && (
                  <div className="mt-2 flex justify-center">
                    <img src={formData.image} alt="Preview" className="h-24 w-24 object-cover rounded-full" />
                  </div>
                )}
              </div>
              <div>
                <Label>Specialty *</Label>
                <Input
                  value={formData.specialty}
                  onChange={(e) => setFormData({ ...formData, specialty: e.target.value })}
                  placeholder="e.g., Odia Cuisine"
                  required
                />
              </div>
              <div>
                <Label>Bio</Label>
                <Textarea
                  value={formData.bio}
                  onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                  placeholder="Brief bio about the chef..."
                  className="min-h-[80px]"
                />
              </div>
              <div>
                <Label>Display Order</Label>
                <Input
                  type="number"
                  value={formData.order}
                  onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) || 0 })}
                />
              </div>
              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>Cancel</Button>
                <Button type="submit" className="btn-emerald" data-testid="save-chef-btn">
                  {editingItem ? 'Update' : 'Create'} Profile
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {chefs.map((item, index) => (
          <motion.div
            key={item.id}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.05 }}
          >
            <Card className={!item.is_active ? 'opacity-60' : ''} data-testid={`chef-card-${item.id}`}>
              <CardContent className="p-4">
                <div className="flex items-start gap-4">
                  <div className="w-20 h-20 rounded-full overflow-hidden flex-shrink-0">
                    <img src={item.image} alt={item.name} className="w-full h-full object-cover" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-serif text-lg truncate">{item.name}</h3>
                    <p className="text-sm text-muted-foreground">{item.role}</p>
                    <Badge variant="outline" className="mt-1">{item.specialty}</Badge>
                  </div>
                </div>
                {item.bio && (
                  <p className="text-sm text-muted-foreground mt-3 line-clamp-2">{item.bio}</p>
                )}
                <div className="flex items-center justify-end gap-2 mt-4 pt-3 border-t">
                  <Switch
                    checked={item.is_active}
                    onCheckedChange={() => toggleActive(item)}
                  />
                  <Button variant="outline" size="sm" onClick={() => handleEdit(item)}>
                    <Pencil className="w-4 h-4" />
                  </Button>
                  <Button variant="outline" size="sm" className="text-red-600" onClick={() => handleDelete(item.id)}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        ))}
      </div>

      {chefs.length === 0 && (
        <div className="text-center py-12 text-muted-foreground">
          <ChefHat className="w-12 h-12 mx-auto mb-4 opacity-50" />
          <p>No chefs added yet</p>
        </div>
      )}
    </div>
  );
}

// ============== MAIN COMPONENT ==============
export default function AdminDining() {
  const { token } = useAuth();
  const [activeTab, setActiveTab] = useState('restaurants');

  const handleSeedData = async () => {
    try {
      const response = await axios.post(`${API_URL}/api/dining/seed`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Seeded: ${response.data.seeded.restaurants} restaurants, ${response.data.seeded.menu_items} menu items, ${response.data.seeded.chefs} chefs`);
      // Refresh the page to show new data
      window.location.reload();
    } catch (error) {
      toast.error('Failed to seed dining data');
    }
  };

  return (
    <div data-testid="admin-dining-page">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
        <div>
          <h1 className="font-serif text-3xl text-foreground">Dining Management</h1>
          <p className="text-muted-foreground mt-1">Manage restaurants, menu items, and chefs</p>
        </div>
        <Button variant="outline" onClick={handleSeedData} data-testid="seed-dining-btn">
          Seed Default Data
        </Button>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="mb-6">
          <TabsTrigger value="restaurants" className="flex items-center gap-2">
            <Store className="w-4 h-4" />
            Restaurants
          </TabsTrigger>
          <TabsTrigger value="menu" className="flex items-center gap-2">
            <UtensilsCrossed className="w-4 h-4" />
            Menu
          </TabsTrigger>
          <TabsTrigger value="chefs" className="flex items-center gap-2">
            <ChefHat className="w-4 h-4" />
            Chefs
          </TabsTrigger>
        </TabsList>

        <TabsContent value="restaurants">
          <RestaurantsTab />
        </TabsContent>
        <TabsContent value="menu">
          <MenuTab />
        </TabsContent>
        <TabsContent value="chefs">
          <ChefsTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}
